import { useCallback, useMemo } from 'react';
import { Stack } from '@optimus-web/core';
import { Reservation } from '@mews-commander/data-access';
import { DefaultErrorBoundary } from '@mews-commander/ui';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import {
    useInvalidateReservationGroupAllQuery,
    useInvalidateReservationGroupQuery,
} from '@mews-commander/core/entities';
import { getCancelledCheckedReservationsCount, getCheckedReservationIds } from '../../selectors';
import { fetchCurrentEnterpriseCheckedReservations, fetchGroupNonCanceledReservations } from '../../actions';
import { RESERVATION_BARE_MINIMUM_EXTENT, RESERVATION_ORDER_ITEMS_EXTENT } from '../../constants';
import {
    selectionIsCheckedIn as selectionIsCheckedInSelector,
    selectionIsMultipleNightsLong as selectionIsMultipleNightsLongSelector,
    selectionIsUnconfirmed as selectionIsUnconfirmedSelector,
} from '../../selectors/reservationModuleState/reservationSelection';
import { IntervalAdjustmentForm } from './IntervalAdjustmentForm/IntervalAdjustmentForm';
import { AssignedCategoryForm } from './AssignedCategoryForm/AssignedCategoryForm';
import { TravelAgencyForm } from './TravelAgencyForm/TravelAgencyForm';
import { PurchaseOrderNumberForm } from './PurchaseOrderNumberForm/PurchaseOrderNumberForm';
import { CompanyForm } from './CompanyForm/CompanyForm';
import { TravelAgencyNumberForm } from './TravelAgencyNumberForm/TravelAgencyNumberForm';
import { showChannelNumber } from './selectors';
import { ReservationPurposeForm } from './ReservationPurposeForm/ReservationPurposeForm';
import { NotesForm } from './NotesForm/NotesForm';
import { SplitReservationForm } from './SplitReservationForm/SplitReservationForm';
import { BusinessSegmentForm } from './BusinessSegmentForm/BusinessSegmentForm';
import { OriginForm } from './OriginForm/OriginForm';
import { ReleaseForm } from './ReleaseForm/ReleaseForm';
import { BookerForm } from './BookerForm/BookerForm';
import { AgeCategoriesCountForm } from './AgeCategoriesCountForm/AgeCategoriesCountForm';

interface ReservationsOptions {
    origin: boolean;
    orderItems?: boolean;
    products?: boolean;
    partnerCompanies?: boolean;
    travelAgencies?: boolean;
    generalNotes?: boolean;
    channelManagerNotes?: boolean;
    accounts?: boolean;
}

const PropertiesTab = ({
    scope,
    serviceId,
    firstCheckedReservationAvailabilityBlockId,
    checkedReservationsHaveCommanderServiceOrderOrigin,
}: {
    scope: string;
    serviceId: string;
    firstCheckedReservationAvailabilityBlockId: Reservation['availabilityBlockId'];
    checkedReservationsHaveCommanderServiceOrderOrigin: boolean;
}) => {
    const isReservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const dispatch = useCommanderThunkDispatch();

    const reservationIds = useCommanderSelector((state) => getCheckedReservationIds(state, { scope }));
    const cancelledReservantionsCount = useCommanderSelector((state) =>
        getCancelledCheckedReservationsCount(state, { scope })
    );
    const hasCancelledReservations = !!cancelledReservantionsCount;

    const showTravelAgencyNumberForm = useCommanderSelector((state) => showChannelNumber(state, { scope }));

    const selectionIsCheckedIn = useCommanderSelector((state) => selectionIsCheckedInSelector(state, { scope }));
    const selectionIsMultipleNightsLong = useCommanderSelector((state) =>
        selectionIsMultipleNightsLongSelector(state, { scope })
    );

    const showSplitReservationForm = useMemo(
        () => selectionIsCheckedIn && selectionIsMultipleNightsLong,
        [selectionIsCheckedIn, selectionIsMultipleNightsLong]
    );

    const selectionIsUnconfirmed = useCommanderSelector((state) => selectionIsUnconfirmedSelector(state, { scope }));

    const refetchReservations = useCallback(
        (extent: ReservationsOptions = RESERVATION_ORDER_ITEMS_EXTENT) => {
            dispatch(fetchCurrentEnterpriseCheckedReservations({ scope, extent }));
        },
        [dispatch, scope]
    );
    const invalidateReservations = useInvalidateReservationGroupQuery(scope, { invalidateOrderItemsQuery: false });
    const invalidateReservationsOrderItems = useInvalidateReservationGroupQuery(scope, {
        invalidateOrderItemsQuery: true,
    });
    const invalidateAllReservationQueries = useInvalidateReservationGroupAllQuery(scope);
    const intervalAdjustmentRefetchReservations = useCallback(
        (createNewReservation: boolean) => {
            if (createNewReservation) {
                if (isReservationModuleReactQueryEnabled) void invalidateAllReservationQueries();
                else dispatch(fetchGroupNonCanceledReservations({ groupId: scope }));
            } else if (isReservationModuleReactQueryEnabled) void invalidateReservationsOrderItems();
            else refetchReservations();
        },
        [
            isReservationModuleReactQueryEnabled,
            invalidateReservationsOrderItems,
            refetchReservations,
            invalidateAllReservationQueries,
            dispatch,
            scope,
        ]
    );

    const splitReservationRefetchReservations = useCallback(() => {
        if (isReservationModuleReactQueryEnabled) void invalidateAllReservationQueries();
        else dispatch(fetchGroupNonCanceledReservations({ groupId: scope }));
    }, [isReservationModuleReactQueryEnabled, invalidateAllReservationQueries, dispatch, scope]);

    return (
        <DefaultErrorBoundary>
            <Stack spacing="200" vertical>
                <NotesForm
                    scope={scope}
                    reservationIds={reservationIds}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () =>
                                  refetchReservations({
                                      ...RESERVATION_BARE_MINIMUM_EXTENT,
                                      generalNotes: true,
                                      channelManagerNotes: true,
                                  })
                    }
                />

                <IntervalAdjustmentForm
                    reservationIds={reservationIds}
                    onSuccess={intervalAdjustmentRefetchReservations}
                    serviceId={serviceId}
                />
                {showSplitReservationForm && (
                    <SplitReservationForm
                        reservationIds={reservationIds}
                        scope={scope}
                        onSuccess={splitReservationRefetchReservations}
                    />
                )}
                {selectionIsUnconfirmed && (
                    <ReleaseForm
                        reservationIds={reservationIds}
                        scope={scope}
                        onSuccess={
                            isReservationModuleReactQueryEnabled
                                ? invalidateReservations
                                : () => refetchReservations(RESERVATION_BARE_MINIMUM_EXTENT)
                        }
                    />
                )}
                <AgeCategoriesCountForm
                    scope={scope}
                    serviceId={serviceId}
                    reservationIds={reservationIds}
                    onSuccess={
                        isReservationModuleReactQueryEnabled ? invalidateReservationsOrderItems : refetchReservations
                    }
                />
                {!hasCancelledReservations && (
                    <AssignedCategoryForm serviceId={serviceId} reservationIds={reservationIds} scope={scope} />
                )}
                <ReservationPurposeForm
                    scope={scope}
                    reservationIds={reservationIds}
                    availabilityBlockId={firstCheckedReservationAvailabilityBlockId}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () => refetchReservations(RESERVATION_BARE_MINIMUM_EXTENT)
                    }
                />
                <BusinessSegmentForm
                    scope={scope}
                    reservationIds={reservationIds}
                    serviceId={serviceId}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () => refetchReservations(RESERVATION_BARE_MINIMUM_EXTENT)
                    }
                />
                {checkedReservationsHaveCommanderServiceOrderOrigin && (
                    <OriginForm
                        scope={scope}
                        reservationIds={reservationIds}
                        onSuccess={
                            isReservationModuleReactQueryEnabled
                                ? invalidateReservations
                                : () => refetchReservations(RESERVATION_BARE_MINIMUM_EXTENT)
                        }
                    />
                )}
                <BookerForm
                    scope={scope}
                    reservationIds={reservationIds}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () => refetchReservations({ ...RESERVATION_BARE_MINIMUM_EXTENT, accounts: true })
                    }
                />
                <CompanyForm
                    scope={scope}
                    reservationIds={reservationIds}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () => refetchReservations({ ...RESERVATION_BARE_MINIMUM_EXTENT, partnerCompanies: true })
                    }
                />
                <TravelAgencyForm
                    scope={scope}
                    reservationIds={reservationIds}
                    serviceId={serviceId}
                    onSuccess={
                        isReservationModuleReactQueryEnabled
                            ? invalidateReservations
                            : () => refetchReservations({ ...RESERVATION_BARE_MINIMUM_EXTENT, travelAgencies: true })
                    }
                />
                <PurchaseOrderNumberForm reservationIds={reservationIds} />
                {showTravelAgencyNumberForm && (
                    <TravelAgencyNumberForm
                        scope={scope}
                        reservationIds={reservationIds}
                        onSuccess={
                            isReservationModuleReactQueryEnabled
                                ? invalidateReservations
                                : () => refetchReservations(RESERVATION_BARE_MINIMUM_EXTENT)
                        }
                    />
                )}
            </Stack>
        </DefaultErrorBoundary>
    );
};

export default PropertiesTab;
